import React from "react";
import { useField, useFormikContext } from "formik";
import { Flex, Text, TextInput } from "@jsq/ds/core";
import { CountrySelectionDropDown } from "../../../../common/components/CountrySelectionDropDown";
import { MultiInputFormRow } from "../../../../common/components/Form/Layout/MultiInputFormRow";
import { getCountryValue } from "../../../../utils/country_utils";
import { validateTaxId, TAX_ID_VALIDATION_ERRORS } from "./constants";
import { type FormIntlTaxIdInputProps, AccountType } from "./types";

/**
 * FormIntlTaxIdInput component with EIN-only validation for US entities.
 * Prevents SSN/ITIN submission per FF-4166 requirements.
 *
 * Note: This is largely based on the design system's FormIntlTaxIdInput component,
 * but adapted for reliable form submission with Formik. The design system component
 * has form submission issues that prevent proper integration with jsq-payments-components.
 */
export const FormIntlTaxIdInput: React.FC<FormIntlTaxIdInputProps> = ({
  taxIdFieldName,
  taxCountryFieldName,
  label = "Tax ID",
  optional = true,
  placeholder = "Enter tax ID number",
  className,
  accountType = AccountType.Entity,
  skipValidation = false,
  displayErrorMessage = true,
}) => {
  const { values } = useFormikContext<Record<string, unknown>>();

  const validateTaxIdField = (value: string): string | undefined => {
    if (skipValidation) return undefined;

    const taxCountry = (values[taxCountryFieldName] as string) || "";
    const hasValue = value && value.trim().length > 0;
    const hasCountry = taxCountry && taxCountry.length > 0;

    // If neither field has a value, that's valid (both are optional)
    if (!hasValue && !hasCountry) {
      return undefined;
    }

    // If country is selected but tax ID is empty, that's an error
    if (hasCountry && !hasValue) {
      return TAX_ID_VALIDATION_ERRORS.REQUIRED_WHEN_COUNTRY_PROVIDED;
    }

    // If tax ID is provided but country is empty, that's an error
    if (hasValue && !hasCountry) {
      return TAX_ID_VALIDATION_ERRORS.COUNTRY_REQUIRED;
    }

    // If both are provided, validate the format
    if (hasValue && hasCountry) {
      const validation = validateTaxId(value, taxCountry, accountType);
      if (!validation.isValid && validation.error) {
        return validation.error;
      }
    }

    return undefined;
  };

  const [, , countryHelpers] = useField(taxCountryFieldName);
  const [taxIdField, taxIdMeta] = useField({
    name: taxIdFieldName,
    validate: validateTaxIdField,
  });

  const handleCountryChange = (value: string | number | string[] | number[]) => {
    void countryHelpers.setValue(value);
  };

  const handleTaxIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    taxIdField.onChange(e);
  };

  return (
    <Flex flexDirection="column" gap={6} className={className}>
      <Flex gap={4}>
        <Text variant="strong-md">{label}</Text>
        {optional && <Text variant="secondary-md">(optional)</Text>}
      </Flex>

      <MultiInputFormRow>
        <CountrySelectionDropDown
          name={taxCountryFieldName}
          placeholder="Select a country"
          handleChange={handleCountryChange}
          getValue={getCountryValue}
          isFull={true}
        />
        <TextInput
          name={taxIdFieldName}
          placeholder={placeholder}
          isFullWidth
          value={(taxIdField.value as string) || ""}
          onChange={handleTaxIdChange}
          onBlur={taxIdField.onBlur}
          errorMessage={
            displayErrorMessage && taxIdMeta.touched && taxIdMeta.error
              ? taxIdMeta.error
              : undefined
          }
        />
      </MultiInputFormRow>
    </Flex>
  );
};

// Re-export types for convenience
export { AccountType } from "./types";

export default FormIntlTaxIdInput;
